import React, { useCallback } from "react";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import RscHbCardEvent from "~components/molecules/rsc_hb_card/rsc_hb_card_event/rsc_hb_card_event";
import RscHbCardEventFeatured from "~components/molecules/rsc_hb_card/rsc_hb_card_event_featured/rsc_hb_card_event_featured";
import RscHbCtaNewsletter from "~components/molecules/rsc_hb_cta/rsc_hb_cta_newsletter/rsc_hb_cta_newsletter";
import RscHbInPageContactForm from "~components/molecules/rsc_hb_in_page_contact_form/rsc_hb_in_page_contact_form";
import RscHbPageHeader from "~components/molecules/rsc_hb_page_header/rsc_hb_page_header";
import RscHbPageWrapper from "~components/molecules/rsc_hb_page_wrapper/rsc_hb_page_wrapper";
import RscHbPaginatedList from "~components/molecules/rsc_hb_paginated_list/rsc_hb_paginated_list";
import usePageData from "~hooks/use_page_data/use_page_data";
import {
  RESOURCE_HUB_SECTION,
  STORYBLOK_ENTRY,
  STORYBLOK_ENTRY_ARRAY,
} from "../../prop_types";
import TemplateGlobalEntry from "../template_global_entry/template_global_entry";
import RscHbSectionInner from "../template_resource_hub/components/rsc_hb_section_inner/rsc_hb_section_inner";

const GRID_SPLIT = { XXL: "3", S: "2", XS: "1" };

export default function TemplateListEvents(props) {
  const { story, pageContext, entryAssets } = usePageData(props);

  const {
    breadcrumbsItems,
    contactForm,
    currentPage,
    itemsPerPage,
    lang,
    pageCount,
    sections,
    pagePath,
    subNavData,
    UIStrings,
    navigationConfig,
    resourceHubCtaConfig,
  } = pageContext;

  const { paginationSectionObject, featuredItem: featuredItemObject } =
    sections;
  const [featuredItem] = featuredItemObject?.items || [];

  const { title, subtitle, featuredItemTitle, allItemsTitle } = story;

  const isAtTopLevel = currentPage === 1;

  const { ctaNewsletter } = resourceHubCtaConfig || {};

  const CtaComponent = useCallback(() => {
    return <RscHbCtaNewsletter {...ctaNewsletter} />;
  }, [ctaNewsletter]);

  return (
    <TemplateGlobalEntry
      subNavData={subNavData}
      entryAssets={entryAssets}
      {...props}
      headerColor="blue"
      pagePath={pagePath}
      programmaticPageTitle={title}
      isListPage
    >
      <RscHbPageWrapper
        items={subNavData}
        lang={lang}
        level1ActiveItem="events"
        navigationConfig={navigationConfig}
      >
        <RscHbPageHeader
          breadcrumbsItems={breadcrumbsItems}
          title={title}
          description={subtitle}
        />

        {/* ————— FEATURED ITEM ————— */}

        {featuredItem && isAtTopLevel && (
          <RscHbSectionInner title={featuredItemTitle}>
            <RscHbCardEventFeatured {...featuredItem} />
          </RscHbSectionInner>
        )}

        {/* ————— ALL EVENTS SECTION ————— */}
        <RscHbPaginatedList
          allItemsTitle={allItemsTitle}
          basePath={pagePath}
          contentCardComponent={RscHbCardEvent}
          ctaComponent={CtaComponent}
          currentPage={currentPage}
          gridSplit={GRID_SPLIT}
          items={paginationSectionObject.items}
          itemsPerPage={itemsPerPage}
          pageCount={pageCount}
          UIStrings={UIStrings}
        />
      </RscHbPageWrapper>
      <RscHbInPageContactForm lang={lang} {...contactForm} />
    </TemplateGlobalEntry>
  );
}

TemplateListEvents.propTypes = {
  data: PropTypes.shape({
    assets: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    items: STORYBLOK_ENTRY_ARRAY,
    story: STORYBLOK_ENTRY,
  }).isRequired,
  pageContext: PropTypes.shape({
    breadcrumbsItems: PropTypes.arrayOf(PropTypes.shape({})),
    contactForm: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
    }),
    UIStrings: PropTypes.shape({}),
    currentPage: PropTypes.number,
    sections: PropTypes.shape({
      paginationSectionObject: RESOURCE_HUB_SECTION,
      featuredItem: RESOURCE_HUB_SECTION,
    }),
    itemsPerPage: PropTypes.number,
    lang: PropTypes.string,
    pageCount: PropTypes.number,
    pagePath: PropTypes.string,
    subNavData: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

// TODO: get page specific metadata here
export const query = graphql`
  query TemplateListEventsQuery(
    $id: String
    $langRegex: String
    $allCoverIDs: [String]
  ) {
    ...resourceHubStory
    ...global
    ...resourceHubCoverAssets
  }
`;
